<template>
  <v-dialog v-model="openActions" persistent max-width="800">
    <v-card>
      <v-card-title>Credit Note Proposal</v-card-title>

      <v-card-text>
        <v-row>
          <v-col>
            <v-row>
              <v-col cols="12" md="4">
                <v-select v-model="account.product_type" label="Product Type" :items="productTypes" :hide-details="!$v.account.product_type.$error" :readonly="noStatus(account.approval_status)" :error-messages="productTypeErrors" @change="$v.account.product_type.$touch()" @blur="$v.account.product_type.$touch()" :disabled="isLoading" outlined dense></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-text-field v-model="account.amount" label="Amount" prefix="£" dense outlined :disabled="disableAmount" :hide-details="!$v.account.amount.$error" :readonly="noStatus(account.approval_status)" :error-messages="amountFieldErrors" @blur="$v.account.amount.$touch()" @input="$v.account.amount.$touch()"></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field v-model="subtotal" label="Subtotal" prefix="£" disabled outlined dense class="mb-n6"></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field v-model="vat" label="VAT" prefix="£" disabled outlined dense class="mb-n6"></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-select v-model="account.short_reason" label="Short Reason" :items="getCreditNoteReasons" :hide-details="!$v.account.short_reason.$error" :error-messages="shortReasonErrors" :readonly="noStatus(account.approval_status)" @change="$v.account.short_reason.$touch()" @blur="$v.account.short_reason.$touch()" :disabled="isLoading||(!hasUserGotRoles(['credit-note-admin','credit-note-team-leader','credit-note-manager']))" outlined dense></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-textarea v-model="account.detailed_reason" label="Detailed Reason" auto-grow dense outlined :disabled="isLoading||(!hasUserGotRoles(['credit-note-admin','credit-note-team-leader','credit-note-manager']))" :readonly="noStatus(account.approval_status)" :hide-details="!$v.account.detailed_reason.$error" :error-messages="detailReasonFieldErrors" @blur="$v.account.detailed_reason.$touch()" @input="$v.account.detailed_reason.$touch()"></v-textarea>
              </v-col>
            </v-row>
          </v-col>
          
          <v-col>
            <v-card>
              <v-card-text>
                <v-card-title class="pa-0">
                  Billing Account
                  <v-spacer></v-spacer>
                  <v-btn class="mt-0" color="secondary" x-small :to="`/accounts/${account.account.id}`" target="_blank">
                    <v-icon>{{mdiOpenInNew}}</v-icon>
                  </v-btn>
                </v-card-title>
                <v-row>
                  <v-col cols="12" md="7">
                    <span>Account ID</span><br>
                    <span class="text-h3">
                      <a class="accent--text" :href="`/accounts/${account.account.id}`">{{ account.account.id | textPlaceholder }}</a>
                    </span>
                  </v-col>
                  <v-col cols="12" md="5">
                    <span>Bill Cycle</span><br>
                    <span class="text-h3 accent--text">{{ account.account.bill_cycle | textPlaceholder }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="7">
                    <span>Business Name</span><br>
                    <span class="text-h6">{{ account.account.business_name | textPlaceholder }}</span>
                  </v-col>
                  <v-col cols="12" md="5">
                    <span>Customer Name</span><br>
                    <span class="text-h6">{{ getFullName | textPlaceholder }}</span>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-card flat class="mt-1">
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="7">
                    <p class="mb-1 text-caption">Pre-approval Status</p>
                    <p><b>{{account.pre_approval_status}}</b></p>
                    <p class="mb-1 text-caption">Pre-approver</p>
                    <p><b>{{account.pre_approver_sid | textPlaceholder}}</b></p>
                    <p class="mb-1 text-caption">Pre-approval Date</p>
                    <p><b>{{account.pre_approved_timestamp | formatDate | textPlaceholder}}</b></p>
                  </v-col>
                  <v-col cols="12" md="5">
                    <p class="mb-1 text-caption">Approval Status</p>
                    <p><b>{{account.approval_status}}</b></p>
                    <p class="mb-1 text-caption">Approver</p>
                    <p><b>{{account.approver_sid | textPlaceholder}}</b></p>
                    <p class="mb-1 text-caption">Approval Date</p>
                    <p><b>{{account.approved_timestamp | formatDate | textPlaceholder}}</b></p>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <template v-if="isSalesCreditSelected">
          <v-divider class="my-2"></v-divider>

          <v-row>
            <v-col>
              <LineItemsTable :showEditItems="account.approval_status!=='APPROVED'"/>
            </v-col>
          </v-row>
        </template>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <div v-if="hasUserGotRoles(['credit-note-admin','credit-note-manager'])||showCreditNoteLeader">
          <v-btn color="secondary" small class="mr-2" :disabled="updateCreditNotesProposalsLoading || noStatus(account.approval_status)" :loading="loaders.approve" @click="submitAction('approve', account)">Approve</v-btn>
          <v-btn color="body" small class="white--text mr-2" :disabled="updateCreditNotesProposalsLoading || noStatus(account.approval_status)" :loading="loaders.decline" @click="submitAction('decline', account)">Decline</v-btn>
        </div>

        <div v-if="showCreditNoteLeaderActions">
          <v-btn color="secondary" small class="mr-2" :disabled="updateCreditNotesProposalsLoading || noStatus(account.pre_approval_status) || noStatus(account.approval_status)" :loading="loaders.approve" @click="submitAction('pre_approve', account)">Pre-approve</v-btn>
          <v-btn color="body" small class="white--text" :disabled="updateCreditNotesProposalsLoading || noStatus(account.pre_approval_status) || noStatus(account.approval_status)" :loading="loaders.decline" @click="submitAction('pre_decline', account)">Pre-decline</v-btn>
        </div>

        <v-spacer></v-spacer>

        <template v-if="showError">
          <div class="mr-2 error--text text-uppercase">Please select at least 1 item</div>
        </template>

        <v-btn color="primary" small :disabled="updateCreditNotesProposalsLoading || noStatus(account.approval_status)" :loading="loaders.save" @click="submitAction('save', account)">Save</v-btn>
        <v-btn color="primary" small outlined :disabled="updateCreditNotesProposalsLoading" class="ml-3" @click="closeDialog">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import { mdiOpenInNew } from '@mdi/js';
import { mapState, mapMutations, mapActions, mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { required, decimal } from "vuelidate/lib/validators";
import { addVat } from '@/helpers';

import LineItemsTable from './LineItemsTable';

export default {
  components: { LineItemsTable },

  mixins: [validationMixin],

  validations: {
    account: {
      amount: { required, decimal },
      detailed_reason: { required },
      product_type: { required },
      short_reason: { required },
    },
  },
  
  data () {
    return {
      mdiOpenInNew,
      account: {
        account: {
          first_name: "",
          last_name: "",
          bill_cycle: "",
        },
        amount: "",
        product_type: "",
        short_reason: "",
        detailed_reason: ""
      },
      loaders: {
        save: false,
        approve: false,
        decline: false,
      },
      showCreditNoteLeader: false,
      showCreditNoteLeaderActions: false,
      productTypes:['VoIP','Telco','Website','Bundle'],
      showError: false,
      addVat
    }
  },

  props: {
    openActions: {
      type: Boolean,
      default: false
    },
    fetchCreditNotes: {
      type: Boolean,
      default: false
    }
  },
  
  watch: {
    creditNotesProposalsActionAccount: {
      deep: true,
      immediate: true,
      handler(account) {
        if(account) {
          Object.assign(this.account, account);
          if(this.hasUserGotRoles(['credit-note-team-leader'])){ this.checkLeaderRole(); }
        }
      }
    },

    async openActions(val) {
      if (val) {
        if (this.isSalesCreditSelected) {
          
          if (this.fetchCreditNotes) {
            await this.fetchCreditNoteReasonsByAccount(this.getCreditNotesProposalsActionAccount.account_id);
          }
          this.UPDATE_LINE_ITEMS({lineItems: this.getFormatLineItems, totalCreditAmount: this.subtotal} );
        }
        await this.fetchCreditNoteReasons();
      }
    },

    isSalesCreditSelected(val) {
      if (val) {
        this.account.amount = this.addVat(this.getTotalCreditAmount);
        this.$v.account.amount.$reset();       
      } else {
        this.account.amount = '';
      }
    },

    getTotalCreditAmount(val) {
      if (this.isSalesCreditSelected) {
        const valIncludingVat = this.addVat(val);
        this.account.amount = valIncludingVat < 0 ? valIncludingVat : -valIncludingVat;
      }
    }
  },

  computed: {
    ...mapState("creditnotes", ["creditNotesProposalsActionAccount", "updateCreditNotesProposalsLoading"]),
    ...mapGetters("creditnotes", ["getCreditNoteReasons", 'getTotalCreditAmount', 'getLineItems', 'getLineItemsForSubmission', 'getFormatLineItems', 'getCreditNotesProposalsActionAccount']),
    ...mapGetters("globals", ["hasUserGotRoles"]),

    isLoading() {
      return Object.values(this.loaders).some(e => e === true);
    },

    getFullName() {
      return `${this.account.account.first_name} ${this.account.account.last_name}`;
    },

    productTypeErrors() {
      const errors = [];
      if (!this.$v.account.product_type.$dirty) return errors;
      !this.$v.account.product_type.required && errors.push("Product type is required");
      return errors;
    },

    amountFieldErrors() {
      const errors = [];
      if (!this.$v.account.amount.$dirty) return errors;
      !this.$v.account.amount.required && errors.push("Amount is required");
      !this.$v.account.amount.decimal && errors.push("Amount must be a number");
      return errors;
    },

    shortReasonErrors() {
      const errors = [];
      if (!this.$v.account.short_reason.$dirty) return errors;
      !this.$v.account.short_reason.required && errors.push("Short reason type is required");
      return errors;
    },

    detailReasonFieldErrors() {
      const errors = [];
      if (!this.$v.account.detailed_reason.$dirty) return errors;
      !this.$v.account.detailed_reason.required && errors.push("Detailed Reason is required");
      return errors;
    },

    vat() {
      let vat = this.account.amount - (this.account.amount / (1+this.$vatAmount));
      return vat.toFixed(2);
    },

    subtotal() {
      let total = this.account.amount - this.vat;
      return total.toFixed(2);
    },

    disableAmount() {
      return this.isLoading || !this.hasUserGotRoles(['credit-note-admin','credit-note-team-leader','credit-note-manager']) || this.isSalesCreditSelected;
    },

    isSalesCreditSelected() {
      return this.account.short_reason === 'Sales Credit';
    }
  },

  filters: {
    textPlaceholder(val) {
      if(val === null || val === "" || val === undefined) return "-";
      return val;
    },
    formatDate(val) {
      if(!val) return "-";
      const date = new Date(val);
      return moment(date).format('YYYY/MM/DD');
    },
  },

  methods: {
    ...mapMutations('creditnotes', ['UPDATE_LINE_ITEMS', 'RESET_CREDIT_NOTES_PROPOSALS_ACTION_ACCOUNT']),
    ...mapActions("creditnotes", ["updateCreditNoteProposalsPagination", "updateCreditNoteProposal", 'fetchCreditNoteReasons', 'fetchCreditNoteReasonsByAccount']),

    checkLeaderRole(){
      const CREDIT_NOTE_TEAM_LEADER_LIMIT = 35;
      const CREDIT_NOTE_USER_LIMIT = 3;
      let acc = this.account;
      let amt = acc.amount;
      if(acc.amount>0){ amt = acc.amount*-1; }
      this.showCreditNoteLeader=(amt>=-CREDIT_NOTE_TEAM_LEADER_LIMIT&&amt<=-CREDIT_NOTE_USER_LIMIT);
      this.showCreditNoteLeaderActions=(amt<-CREDIT_NOTE_TEAM_LEADER_LIMIT);
    },

    noStatus(val) {
      if(!val) return true;
      if(val.toLowerCase() !== 'none') return true;
    },

    closeDialog(){
      this.reset();
      this.$emit('closeDialog');
      Object.assign(this.account, this.creditNotesProposalsActionAccount);
    },

    reset() {
      this.showError = false;
      this.UPDATE_LINE_ITEMS({lineItems: [], totalCreditAmount: 0});
      this.RESET_CREDIT_NOTES_PROPOSALS_ACTION_ACCOUNT();
      this.account.account.first_name = '';
      this.account.account.last_name = '';
      this.account.account.bill_cycle = '';
      this.account.amount = '';
      this.account.product_type = '';
      this.account.short_reason = '';
      this.account.detailed_reason = '';
      this.$v.$reset();
    },

    async submitAction(action, account) {
      this.validateLineItems();

      if (!this.showError) {
        const payload = {
          product_type: account.product_type,
          short_reason: account.short_reason,
          detailed_reason: account.detailed_reason,
          amount: (account.amount<0)?account.amount:-(account.amount),
          line_items: this.isSalesCreditSelected ? this.getLineItemsForSubmission : []
        };

        switch(action) {
          case 'approve':
            payload['approval_status'] = "APPROVED";
            break;
          case 'decline':
            payload['approval_status'] = "DECLINED";
            break;
          case 'pre_approve':
            payload['pre_approval_status'] = "APPROVED";
            break;
          case 'pre_decline':
            payload['pre_approval_status'] = "DECLINED";
            break;
        }
        
        this.loaders[action] = true;
        try {
          await this.updateCreditNoteProposal({id: account.id, payload});
          this.loaders[action] = false;
          this.fetchCreditNoteReasonsByAccount(this.getCreditNotesProposalsActionAccount.account_id);
          this.closeDialog();
        } catch(err) {
          this.loaders[action] = false;
        }
      }
    },

    validateLineItems() {
      this.showError = this.isSalesCreditSelected && this.getLineItems.length === 0 ? true : false;
    },
  },
}
</script>

<style>
</style>
